// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { generate } from "@ant-design/colors";
import theme, { type GlobalToken } from "antd/es/theme";
import Color from "color";
import { useAtomValue } from "jotai";
import { atomWithDefault } from "jotai/utils";
import { useMemo } from "react";

import { fetchJson } from "../../infra/fetchJson";
import { useColorScheme } from "./preferences";

const DARK_BACKGROUND = "#141414";
const DARK_PRIMARY_INDEX = 5;

interface Token {
  colorPrimary?: string;
  colorBorder?: string;
}

interface Size {
  width?: number;
  height?: number;
}

// https://github.com/lablup/backend.ai-webui/blob/9c536708574bdd19a7c3133b0cbdd0b6ecba1aa9/react/src/helper/customThemeConfig.ts#L5-L22
interface LogoConfig {
  src?: string;
  srcCollapsed?: string;
  srcDark?: string;
  srcCollapsedDark?: string;
  size?: Size;
  sizeCollapsed?: Size;
}

interface ThemeMode {
  token?: Token;
  logo?: LogoConfig;
}

export interface Theme {
  light?: ThemeMode;
  dark?: ThemeMode;
  branding?: {
    companyName?: string;
    brandName?: string;
    loginLogo?: {
      src?: string;
    };
  };
}

const themeAtom = atomWithDefault(async () => {
  const theme: Theme = await fetchJson<Theme>(
    "/static/theme.json",
    {},
    10_000
  ).catch(() => ({}) as Theme);
  return theme;
});

type MaterialToken = {
  colorPrimaryVariant: string;
  colorTextOnPrimary: string;
};

export type AppTheme = {
  token: GlobalToken & MaterialToken;
  isDarkMode: boolean;
  branding: {
    logo: {
      src: string;
      srcCollapsed: string;
      srcDark: string;
      srcCollapsedDark: string;
      size?: Size;
      sizeCollapsed?: Size;
    };
    loginLogo: {
      src: string;
    };
    companyName: string;
    brandName: string;
  };
};

const DEFAULT_BRANDING_LOGO = {
  src: "/static/manifest/backend.ai-fasttrack-white.svg",
  srcCollapsed: "/static/manifest/backend.ai-brand-simple-white.svg",
  srcDark: "/static/manifest/backend.ai-fasttrack-black.svg",
  srcCollapsedDark: "/static/manifest/backend.ai-brand-simple-dark.svg",
  loginSrc: "/static/manifest/backend.ai-fasttrack-text-logo.svg",
  loginSrcDark: "/static/manifest/backend.ai-fasttrack-text-logo-dark.svg",
};

export const useAppTheme = (): AppTheme => {
  const { token } = theme.useToken();
  const uiTheme = useAtomValue(themeAtom);
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === "dark";

  const modeTheme = isDarkMode ? uiTheme?.dark : uiTheme?.light;
  const colorPrimary =
    modeTheme?.token?.colorPrimary ??
    (isDarkMode && uiTheme?.light?.token?.colorPrimary
      ? generate(uiTheme.light.token.colorPrimary, {
          theme: "dark",
          backgroundColor: DARK_BACKGROUND,
        })[DARK_PRIMARY_INDEX]
      : token.colorPrimary);

  return useMemo(
    () => ({
      token: {
        ...token,
        colorPrimary: colorPrimary,
        colorBorder: modeTheme?.token?.colorBorder ?? token.colorBorder,
        colorPrimaryVariant: isDarkMode
          ? Color(colorPrimary).darken(0.21875).hex()
          : Color(colorPrimary).lighten(0.21875).hex(),
        colorTextOnPrimary: Color(colorPrimary).darken(0.1).isDark()
          ? "white"
          : "black",
      },
      isDarkMode,
      branding: {
        logo: {
          src: isDarkMode
            ? (modeTheme?.logo?.src ??
              uiTheme.light?.logo?.src ??
              DEFAULT_BRANDING_LOGO.src)
            : (uiTheme.light?.logo?.src ?? DEFAULT_BRANDING_LOGO.src),
          srcCollapsed: isDarkMode
            ? (modeTheme?.logo?.srcCollapsed ??
              uiTheme.light?.logo?.srcCollapsed ??
              DEFAULT_BRANDING_LOGO.srcCollapsed)
            : (uiTheme.light?.logo?.srcCollapsed ??
              DEFAULT_BRANDING_LOGO.srcCollapsed),
          srcDark:
            uiTheme.light?.logo?.srcDark ?? DEFAULT_BRANDING_LOGO.srcDark,
          srcCollapsedDark:
            uiTheme.light?.logo?.srcCollapsedDark ??
            DEFAULT_BRANDING_LOGO.srcCollapsedDark,
          size: modeTheme?.logo?.size ?? uiTheme.light?.logo?.size,
          sizeCollapsed:
            modeTheme?.logo?.sizeCollapsed ??
            uiTheme.light?.logo?.sizeCollapsed,
        },
        companyName: uiTheme.branding?.companyName ?? "Lablup Inc.",
        brandName: uiTheme.branding?.brandName ?? "Backend.AI",
        loginLogo: {
          src: isDarkMode
            ? DEFAULT_BRANDING_LOGO.loginSrcDark
            : (uiTheme.branding?.loginLogo?.src ??
              DEFAULT_BRANDING_LOGO.loginSrc),
        },
      },
    }),
    [token, uiTheme, isDarkMode, colorPrimary, modeTheme]
  );
};
